import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Clock3,
  Copy,
  Headphones,
  Loader2,
  LogOut,
  MessageCircle,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  WalletCards,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ThemeToggle from '../components/ThemeToggle';

const formatCurrency = (amount) =>
  `UGX ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const paymentChannels = [
  { id: 'mtn', name: 'MTN MoMo', caption: 'Mobile money', icon: Smartphone },
  { id: 'airtel', name: 'Airtel Money', caption: 'Mobile money', icon: WalletCards },
  { id: 'bank', name: 'DTB Bank', caption: 'Bank transfer', icon: Building2 },
];

const WorkspaceAccessPage = () => {
  const { api, user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState('mtn');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [form, setForm] = useState({ amount: '', payer_name: '', payer_phone: '', transaction_reference: '', notes: '' });

  const loadSummary = async () => {
    setLoading(true);
    try {
      const { data } = await backendApi.billing.summary(api);
      setSummary(data);
      setForm((current) => ({
        ...current,
        amount: current.amount || String(data?.suggested_amount_ugx || ''),
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to load payment details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return <Navigate to="/login" replace />;

  const collectionPhone = summary?.collection_phone || '0705784295';
  const latestPayment = summary?.payments?.[0];
  const selectedDetails = selectedChannel === 'bank'
    ? [
        ['Account name', 'Abdul Karim Nsimbe'],
        ['Account number', '7183573002'],
        ['Bank', 'DTB · Main Branch'],
      ]
    : [
        ['Send payment to', collectionPhone],
        ['Account name', 'Abdul Karim Nsimbe'],
        ['Amount', formatCurrency(summary?.suggested_amount_ugx)],
      ];

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    if (!form.amount || !form.payer_phone.trim() || !form.transaction_reference.trim()) {
      toast.error('Enter the amount, payer phone, and transaction reference.');
      return;
    }

    setSubmitting(true);
    try {
      await backendApi.billing.createPayment(api, {
        amount: Number(form.amount),
        payer_name: form.payer_name.trim() || null,
        payer_phone: form.payer_phone.trim(),
        transaction_reference: form.transaction_reference.trim(),
        notes: form.notes.trim() || null,
      });
      toast.success('Payment submitted. We will review it shortly.');
      setForm((current) => ({ ...current, transaction_reference: '', notes: '' }));
      await loadSummary();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Payment submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const checkAccess = async () => {
    setCheckingAccess(true);
    try {
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } finally {
      setCheckingAccess(false);
    }
  };

  const whatsappText = encodeURIComponent(`Hello SurviPOS, I need help activating ${summary?.tenant_name || user.email}.`);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <img src="/survipos%20logo.png" alt="SurviPOS logo" className="h-9 w-9 object-contain" />
            <div><p className="font-heading text-lg font-bold leading-none text-primary">SurviPOS</p><p className="mt-1 text-[10px] text-muted-foreground">Secure billing</p></div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle size="icon" showLabel={false} />
            <Button variant="ghost" size="sm" onClick={logout} className="gap-2"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span></Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="mb-10 max-w-3xl">
          <Badge variant="outline" className="mb-4 gap-2 border-warning/30 bg-warning/10 text-foreground"><Clock3 className="h-3.5 w-3.5 text-warning" /> Subscription expired</Badge>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Restore your workspace</h1>
          <p className="mt-3 text-base leading-7 text-muted-foreground">Make your subscription payment, submit the transaction reference, and we’ll restore access after confirmation. Your business data remains safe.</p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_26rem]">
          <div className="space-y-8">
            <section aria-labelledby="plan-heading">
              <div className="mb-4 flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span><h2 id="plan-heading" className="font-heading text-xl font-semibold">Confirm your plan</h2></div>
              <Card className="border-primary/20 bg-primary/5 shadow-sm">
                <CardContent className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                  <div><div className="flex items-center gap-2"><p className="font-heading text-lg font-bold capitalize">{summary?.billing_cycle || 'Monthly'} SurviPOS</p><Badge className="bg-primary/10 text-primary hover:bg-primary/10">Recommended</Badge></div><p className="mt-1 text-sm text-muted-foreground">Full access to sales, inventory, reports, users and business controls.</p></div>
                  <div className="sm:text-right"><p className="font-heading text-2xl font-bold">{loading ? '—' : formatCurrency(summary?.suggested_amount_ugx)}</p><p className="text-xs text-muted-foreground">per {summary?.billing_cycle === 'yearly' ? 'year' : 'month'}</p></div>
                </CardContent>
              </Card>
            </section>

            <section aria-labelledby="channel-heading">
              <div className="mb-4 flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span><h2 id="channel-heading" className="font-heading text-xl font-semibold">Choose where to pay</h2></div>
              <div className="grid gap-3 sm:grid-cols-3">
                {paymentChannels.map(({ id, name, caption, icon: Icon }) => {
                  const selected = id === selectedChannel;
                  return <button key={id} type="button" onClick={() => setSelectedChannel(id)} className={`min-h-[96px] rounded-xl border p-4 text-left transition-all ${selected ? 'border-primary bg-primary/5 ring-2 ring-primary/15' : 'border-border/70 bg-card hover:border-primary/40'}`} aria-pressed={selected}><div className="flex items-start justify-between"><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}><Icon className="h-4 w-4" /></span>{selected && <CheckCircle2 className="h-5 w-5 text-primary" />}</div><p className="mt-3 text-sm font-semibold">{name}</p><p className="text-xs text-muted-foreground">{caption}</p></button>;
                })}
              </div>

              <Card className="mt-4 border-border/60 bg-card/70">
                <CardContent className="p-5 sm:p-6">
                  <div className="grid gap-5 sm:grid-cols-3">
                    {selectedDetails.map(([label, value]) => <div key={label}><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p><div className="mt-2 flex items-center gap-2"><p className="text-sm font-semibold">{value}</p><Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyValue(value, label)} aria-label={`Copy ${label}`}><Copy className="h-3.5 w-3.5" /></Button></div></div>)}
                  </div>
                </CardContent>
              </Card>
            </section>

            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><p><span className="font-semibold text-foreground">Your data is protected.</span> Expiry pauses access only. Nothing in your workspace is deleted while payment is being confirmed.</p></div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-6">
            <Card className="border-border/60 shadow-xl shadow-primary/5">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center justify-between gap-3"><div><p className="text-xs font-medium uppercase tracking-wider text-primary">Final step</p><CardTitle className="mt-1 font-heading text-2xl">Submit payment</CardTitle></div><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><ReceiptText className="h-5 w-5" /></span></div>
              </CardHeader>
              <CardContent className="pt-6">
                {latestPayment?.status === 'pending' && <div className="mb-5 rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm"><div className="flex items-center gap-2 font-semibold"><Clock3 className="h-4 w-4 text-warning" />Payment under review</div><p className="mt-1 text-xs text-muted-foreground">Reference {latestPayment.transaction_reference} was submitted successfully.</p></div>}

                <form onSubmit={submitPayment} className="space-y-4">
                  <div className="space-y-2"><Label htmlFor="billing-amount">Amount paid (UGX)</Label><Input id="billing-amount" type="number" min="1" inputMode="numeric" value={form.amount} onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} placeholder="50000" required className="h-11" /></div>
                  <div className="space-y-2"><Label htmlFor="payer-phone">Phone used to pay</Label><Input id="payer-phone" type="tel" autoComplete="tel" value={form.payer_phone} onChange={(event) => setForm((current) => ({ ...current, payer_phone: event.target.value }))} placeholder="07XXXXXXXX" required className="h-11" /></div>
                  <div className="space-y-2"><Label htmlFor="payment-reference">Transaction reference</Label><Input id="payment-reference" value={form.transaction_reference} onChange={(event) => setForm((current) => ({ ...current, transaction_reference: event.target.value }))} placeholder="e.g. MP24051K9F" required className="h-11" /><p className="text-xs text-muted-foreground">Copy this exactly from your payment message or receipt.</p></div>
                  <div className="space-y-2"><Label htmlFor="payer-name">Payer name <span className="font-normal text-muted-foreground">(optional)</span></Label><Input id="payer-name" autoComplete="name" value={form.payer_name} onChange={(event) => setForm((current) => ({ ...current, payer_name: event.target.value }))} placeholder="Name shown on payment" className="h-11" /></div>
                  <div className="space-y-2"><Label htmlFor="payment-notes">Notes <span className="font-normal text-muted-foreground">(optional)</span></Label><Textarea id="payment-notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Anything we should know" className="min-h-20 resize-none" /></div>
                  <Button type="submit" className="h-12 w-full gap-2 text-base font-semibold shadow-lg shadow-primary/20" disabled={submitting}>{submitting ? <><Loader2 className="h-4 w-4 animate-spin" />Submitting...</> : <>Submit for confirmation<ArrowRight className="h-4 w-4" /></>}</Button>
                </form>

                <div className="mt-5 grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" className="gap-2" onClick={checkAccess} disabled={checkingAccess}>{checkingAccess ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}Check access</Button>
                  <Button variant="outline" asChild className="gap-2"><a href={`https://wa.me/971564575525?text=${whatsappText}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" />Get help</a></Button>
                </div>

                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground"><Headphones className="h-3.5 w-3.5" />Manual payments are reviewed by our support team.</div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default WorkspaceAccessPage;
